import React, { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { IconButton, IconMenu, MenuItem } from 'material-ui';
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';
import * as XLSX from 'xlsx';
import { exportTcIndexes } from 'services/tc/indexes';
import { notifications } from './common';
import { NotificationContext } from './TcIndex';
import { tcIndexFieldsMapping } from 'constants/misc';

/**
 * @typedef {Object} Filters
 * @property {string} [refNo]
 * @property {string} [vesselName]
 * @property {string} [vesselType]
 * @property {string} [vesselDwt]
 * @property {string} [vesselDwtType]
 * @property {string} [vesselOwner]
 * @property {string} [vesselManager]
 */

export default function Misc({ filters }) {
    const addNotification = useContext(NotificationContext);
    const [isQueryEnabled, setIsQueryEnabled] = useState(false);

    const {
        refetch,
    } = useQuery(['exportData', filters], () => exportTcIndexes(filters), { enabled: isQueryEnabled });

    const exportToExcel = async () => {
        setIsQueryEnabled(true);

        const { data, error } = await refetch();

        if (error) {
            addNotification(notifications.error('Could not generate export file.'));
            return;
        }

        if (!data || (data.errors && data.errors.length)) {
            addNotification(notifications.error('Could not generate export file.'));
            return;
        }

        const transformedData = transformArray(data.data);

        const sheet = XLSX.utils.json_to_sheet(transformedData, {
            cellStyles: true,
        });

        const columnWidths = transformedData.reduce((acc, row) => {
            Object.keys(row).forEach((key) => {
                const cellValue = row[key];
                const cellLength = String(cellValue).length;
                acc[key] = Math.max(acc[key] || 0, cellLength);
            });
            return acc;
        }, {});

        sheet['!cols'] = Object.keys(columnWidths)
            .map(key => ({ 
                wch: Math.max(columnWidths[key], key.length),
                style: { alignment: { horizontal: 'left', wrapText: true } },
            }));

        const range = XLSX.utils.decode_range(sheet['!ref']);
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellAddress = { c: C, r: R };
                const cellRef = XLSX.utils.encode_cell(cellAddress);
                if (!sheet[cellRef]) continue;
                
                if (!sheet[cellRef].s) sheet[cellRef].s = {};
                sheet[cellRef].s.alignment = { 
                    horizontal: 'left',
                    wrapText: true,
                };
                
                if (typeof sheet[cellRef].v === 'number') {
                    sheet[cellRef].z = '@';
                }
            }
        }

        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, 'Sheet1');
        const excelBuffer = XLSX.write(book, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'TC Indexes.xlsx';
        a.click();
        URL.revokeObjectURL(url);
        setIsQueryEnabled(false);
    };

    return (
        <IconMenu
            disableAutoFocus
            iconButtonElement={<IconButton><MoreVertIcon /></IconButton>}
            anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
            targetOrigin={{ horizontal: 'left', vertical: 'top' }}
            style={{ display: 'flex', alignItems: 'center' }}
        >
            <MenuItem
                primaryText='Export'
                onClick={exportToExcel}
                style={{
                    margin: '6px 10px', 
                    fontSize: '13px',
                    textAlign: 'center', 
                    color: 'white', 
                    backgroundColor: '#FF4081', 
                }}
            />
        </IconMenu>
    );
}


function transformArray(array) {
    return array.map((obj) => {
        const mapKeys = Array.from(tcIndexFieldsMapping.keys());

        const transformedEntries = mapKeys
            .map((key) => {
                const field = tcIndexFieldsMapping.get(key);
                let value = obj[key];
                
                if (field.isBoolean) {
                    value = obj[key] ? 'Yes' : 'No';
                }

                return [field.placeHolder, value];
            });

        return Object.fromEntries(transformedEntries);
    });
}



// WEBPACK FOOTER //
// ./app/components/TcIndex/Misc.jsx